import React, { useEffect, useState, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
} from '@mui/material';
import CarCard from "../common/CarCard";
import { useLocation, useNavigate } from 'react-router-dom';

const ITEMS_PER_PAGE = 12;

const Products = () => {
  const [carData, setCarData] = useState([]);
  const [filteredCars, setFilteredCars] = useState([]);
  const [visibleCars, setVisibleCars] = useState([]);
  const [make, setMake] = useState('');
  const [bodyType, setBodyType] = useState('');
  const [budget, setBudget] = useState('');
  const [page, setPage] = useState(1);

  const location = useLocation();
  const navigate = useNavigate();

  // Fetch all car data
  useEffect(() => {
    fetch("/assets/carData/carData.json")
      .then((res) => res.json())
      .then((data) => {
        setCarData(data);
      })
      .catch((err) => console.error("Failed to fetch car data", err));
  }, []);

  // Filter cars based on query params
  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const makeParam = query.get("make") || '';
    const bodyParam = query.get("bodyType") || '';
    const budgetParam = query.get("budget") || '';

    setMake(makeParam);
    setBodyType(bodyParam);
    setBudget(budgetParam);
    setPage(1); // reset page on new filters

    let filtered = carData;

    if (makeParam) {
      filtered = filtered.filter((car) => car.make === makeParam);
    }

    if (bodyParam) {
      filtered = filtered.filter((car) => car.bodyType === bodyParam);
    }

    if (budgetParam) {
      if (budgetParam === "under_1L") {
        filtered = filtered.filter((car) => car.price < 100000);
      } else if (budgetParam === "1L_5L") {
        filtered = filtered.filter((car) => car.price >= 100000 && car.price <= 500000);
      } else if (budgetParam === "above_5L") {
        filtered = filtered.filter((car) => car.price > 500000);
      }
    }

    setFilteredCars(filtered);
  }, [location.search, carData]);

  // Load more items on scroll
  const loadMoreCars = useCallback(() => {
    setPage((prevPage) => {
      const nextPage = prevPage + 1;
      const nextCars = filteredCars.slice(0, nextPage * ITEMS_PER_PAGE);
      setVisibleCars(nextCars);
      return nextPage;
    });
  }, [filteredCars]);
  

  // Initial visible items on page/filter change
  useEffect(() => {
    setVisibleCars(filteredCars.slice(0, ITEMS_PER_PAGE));
  }, [filteredCars]);

  // Scroll Event
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const windowHeight = window.innerHeight;
      const fullHeight = document.documentElement.scrollHeight;

      if (scrollTop + windowHeight >= fullHeight - 100) {
        const moreToLoad = filteredCars.length > visibleCars.length;
        if (moreToLoad) {
          loadMoreCars();
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [filteredCars, visibleCars, loadMoreCars]);

  const handleFilterChange = (event, field) => {
    const { value } = event.target;
    if (field === "make") setMake(value);
    if (field === "bodyType") setBodyType(value);
    if (field === "budget") setBudget(value);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (make) params.append("make", make);
    if (bodyType) params.append("bodyType", bodyType);
    if (budget) params.append("budget", budget);
    navigate(`/products?${params.toString()}`);
  };

  return (
    <Container>
      {/* Filter Form */}
      <Box mt={4} mb={4}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth>
                <InputLabel id="make-label">Make</InputLabel>
                <Select
                  labelId="make-label"
                  value={make}
                  onChange={(e) => handleFilterChange(e, "make")}
                  label="Make"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="BMW">BMW</MenuItem>
                  <MenuItem value="Audi">Audi</MenuItem>
                  <MenuItem value="Mercedes">Mercedes</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={3}>
              <FormControl fullWidth>
                <InputLabel id="body-type-label">Body Type</InputLabel>
                <Select
                  labelId="body-type-label"
                  value={bodyType}
                  onChange={(e) => handleFilterChange(e, "bodyType")}
                  label="Body Type"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="SUV">SUV</MenuItem>
                  <MenuItem value="Sedan">Sedan</MenuItem>
                  <MenuItem value="Coupe">Coupe</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={3}>
              <FormControl fullWidth>
                <InputLabel id="budget-label">Budget</InputLabel>
                <Select
                  labelId="budget-label"
                  value={budget}
                  onChange={(e) => handleFilterChange(e, "budget")}
                  label="Budget"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="under_1L">Under ₹1,00,000</MenuItem>
                  <MenuItem value="1L_5L">₹1,00,000 - ₹5,00,000</MenuItem>
                  <MenuItem value="above_5L">Above ₹5,00,000</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={3}>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                fullWidth
                sx={{ height: "100%" }}
              >
                Apply Filters
              </Button>
            </Grid>
          </Grid>
        </form>
      </Box>

      {/* Car List */}
      <Box className="recently-added-section">
        <Box className="section-title-container">
          <Typography variant="h4" className="section-title">
            OUR CARS IN STOCK
          </Typography>
        </Box>
        <Grid container spacing={2}>
          {visibleCars.length > 0 ? (
            visibleCars.map((car) => (
              <Grid item xs={12} sm={6} md={3} key={car.id}>
                <CarCard car={car} />
              </Grid>
            ))
          ) : (
            <Typography variant="body1" sx={{ mt: 2 }}>
              No cars match your criteria.
            </Typography>
          )}
        </Grid>
      </Box>
    </Container>
  );
};

export default Products;
